import {config} from "@/constants/config";
import type {TResponseRequest} from "@/interfaces/http.interface";
import httpRequest from "@/providers/http/request.http";
import {buildQueryUrl} from "@/providers/http/request.instance";
import type {
  AudiencePreview,
  Campaign,
  CampaignFilters,
  CreateCampaignDto,
  DashboardStats,
  PaginatedCampaigns,
  PreviewAudienceDto,
  SendToUserDto,
} from "@/types/push.types";

// Dashboard stats
export const getPushDashboardStatsService = async (): Promise<TResponseRequest<DashboardStats>> => {
  return httpRequest.get<DashboardStats>({
    url: `${config.apiBaseUrl}/bo/push/dashboard`,
  });
};

// Campaign list (paginated with filters)
export const getCampaignsService = async (
  filters: CampaignFilters = {}
): Promise<TResponseRequest<PaginatedCampaigns>> => {
  const raw = await httpRequest.get<PaginatedCampaigns>({
    url: buildQueryUrl(`${config.apiBaseUrl}/bo/push/campaigns`, filters as Record<string, unknown>),
  });
  return raw;
};

// Get campaign detail
export const getCampaignByIdService = async (id: string): Promise<TResponseRequest<Campaign>> => {
  return httpRequest.get<Campaign>({
    url: `${config.apiBaseUrl}/bo/push/campaigns/${encodeURIComponent(id)}`,
  });
};

// Create campaign (draft)
export const createCampaignService = async (body: CreateCampaignDto): Promise<TResponseRequest<Campaign>> => {
  return httpRequest.post<Campaign>({
    url: `${config.apiBaseUrl}/bo/push/campaigns`,
    body: body as unknown as Record<string, unknown>,
  });
};

// Preview audience count
export const previewAudienceService = async (body: PreviewAudienceDto): Promise<TResponseRequest<AudiencePreview>> => {
  return httpRequest.post<AudiencePreview>({
    url: `${config.apiBaseUrl}/bo/push/campaigns/preview-audience`,
    body: body as unknown as Record<string, unknown>,
  });
};

// Send campaign
export const sendCampaignService = async (
  campaignId: string
): Promise<TResponseRequest<{campaignId: string; status: string; totalRecipients: number}>> => {
  return httpRequest.post<{campaignId: string; status: string; totalRecipients: number}>({
    url: `${config.apiBaseUrl}/bo/push/campaigns/${encodeURIComponent(campaignId)}/send`,
    body: {},
  });
};

// Cancel campaign
export const cancelCampaignService = async (
  campaignId: string
): Promise<TResponseRequest<{campaignId: string; status: string}>> => {
  return httpRequest.post<{campaignId: string; status: string}>({
    url: `${config.apiBaseUrl}/bo/push/campaigns/${encodeURIComponent(campaignId)}/cancel`,
    body: {},
  });
};

// Send notification to individual user
export const sendToUserService = async (
  body: SendToUserDto
): Promise<
  TResponseRequest<{campaignId: string; userId: string; status: string; totalSent: number; totalDevices: number}>
> => {
  return httpRequest.post<{
    campaignId: string;
    userId: string;
    status: string;
    totalSent: number;
    totalDevices: number;
  }>({
    url: `${config.apiBaseUrl}/bo/push/notifications/send-to-user`,
    body: body as unknown as Record<string, unknown>,
  });
};
